import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import {
    useReactTable,
    getCoreRowModel,
    createColumnHelper,
    flexRender,
} from '@tanstack/react-table';
import { useNavigate } from "react-router-dom";
import { useDebounce } from "../../components/recruitmentLeader/hook";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Import your existing APIs and utilities
import getAllRequisitionStatus from "../../apis/getallRequisitionStatus/getAllRequisitionStatus.js";
import getAllRecruiterLeaderRequisitionStatusAndCounts from "../../apis/requisitionDetails/getAllRecruiterLeaderRequisitionStatusAndCounts.js";
import getAllRecruiterRequisitionStatusAndCounts from "../../apis/requisitionDetails/getAllRecruiterRequisitionStatusAndCounts.js";
import getRecruiterLeaderRequisitionsDetails from "../../apis/requisitionDetails/getRecruiterLeaderRequisitionsDetails.js";
import getAllRecruiterRequisitions from "../../apis/requisitionDetails/getAllRecruiterRequisitions.js";
import getAllRequisitionStatusAndCounts from "../../apis/requisitionDetails/getAllRequisitionStatusAndCounts.js";
import getRequisitionsDetails from "../../apis/requisitionDetails/getRequisitionsDetails.js";
import getAllTeamLeaderRequisitionStatusAndCounts from "../../apis/requisitionDetails/getAllTeamLeaderRequisitionStatusAndCounts";
import getTeamLeaderRequisitionsDetails from "../../apis/requisitionDetails/getTeamLeaderRequisitionsDetails";
import { filterFunc } from "../GlobalAssistingFunctions/GlobalAssistingFunctions";

import './RequisitionsOverview.css';

const jsonConfig = require("../../Config.json");

const RequisitionsOverview = ({
    idContextTest,
    userRecruitmentpartnerId,
    userRole,
    userType,
    userRecords
}) => {
    const id = idContextTest;
    // Date states - Initialize with current date like original
    const [requisitionsFromDate, setRequisitionsFromDate] = useState(null);
    const [requisitionsToDate, setRequisitionsToDate] = useState(null);

    // Dialog and tab states
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Loading states
    const [loading, setLoading] = useState(false);
    const [tableDataLoadingFilled, setTableDataLoadingFilled] = useState(false);
    const [tableDataLoadingOpen, setTableDataLoadingOpen] = useState(false);
    const [tableDataLoadingCovered, setTableDataLoadingCovered] = useState(false);
    const [tableDataLoadingUncovered, setTableDataLoadingUncovered] = useState(false);
    const [tableDataLoadingUnassigned, setTableDataLoadingUnassigned] = useState(false);

    // Data states
    const [requisitionCount, setRequisitionCount] = useState({
        FILLED: 0, // Keeping original naming from your code
        OPEN: 0,
        COVERED: 0,
        UNCOVERED: 0,
        UNASSIGNED: 0
    });

    const [openRequisitionsDataResponse, setOpenRequisitionsDataResponse] = useState([]);
    const [filledRequisitionDataResponse, setFilledRequisitionDataResponse] = useState([]);

    const [requisitionObj, setRequisitionObj] = useState({
        "FILLED": { offset: 0, limit: 5, data: [] },
        "OPEN": { offset: 0, limit: 5, data: [] },
        "COVERED": { offset: 0, limit: 5, data: [] },
        "UNCOVERED": { offset: 0, limit: 5, data: [] },
        "UNASSIGNED": { offset: 0, limit: 5, data: [] }
    });

    // Hooks
    const debouncedSearchStartDate = useDebounce(requisitionsFromDate, 300);
    const debouncedSearchEndDate = useDebounce(requisitionsToDate, 300);
    const navigate = useNavigate();

    // Utility functions - Using your original function naming
    function convertDate(str) {
        if (!str) return str;
        else {
            let date = new Date(str),
                mnth = ("0" + (date.getMonth() + 1)).slice(-2),
                day = ("0" + date.getDate()).slice(-2);
            return [date.getFullYear(), mnth, day].join("-");
        }
    }

    // Set requisition count data - Keep original logic
    const setRequisitionCountData = (data) => {
        let filledDataCount = data.filter(item => item.name === 'FILLED')[0]?.totalQuantity || 0;
        let coveredDataCount = data.filter(item => item.name === 'COVERED')[0]?.totalQuantity || 0;
        let uncoveredDataCount = data.filter(item => item.name === 'UNCOVERED')[0]?.totalQuantity || 0;
        let unassignedDataCount = data.filter(item => item.name === 'UNASSIGNED')[0]?.totalQuantity || 0;

        setRequisitionCount({
            FILLED: filledDataCount,
            OPEN: coveredDataCount + unassignedDataCount + uncoveredDataCount,
            COVERED: coveredDataCount,
            UNCOVERED: uncoveredDataCount,
            UNASSIGNED: unassignedDataCount
        });
    };

    // Get all requisition status response
    const getAllrequistionStatusResponse = async () => {
        try {
            let response = await getAllRequisitionStatus();
            let openRequisitionsData = response.data.data.filter(item => item.name === 'OPEN')[0];
            setOpenRequisitionsDataResponse(openRequisitionsData);
            let filledRequisitionData = response.data.data.filter(item => item.name === 'FILLED')[0];
            setFilledRequisitionDataResponse(filledRequisitionData);
        } catch (error) {
            console.log("Error", error);
        }
    };

    // Get requisition data function - Keep your original logic
    const getRequisitionData = async (requisitionStatus, isScroll) => {
        try {
            let statusCode = requisitionStatus === "FILLED" ?
                filledRequisitionDataResponse?.statusCode :
                openRequisitionsDataResponse?.statusCode;

            let subStatusCode = "";
            let selectedFilter = {
                "covered": "",
                "priority": "",
                "latestDate": true,
                "uncovered": "",
                "unassigned": "",
                "fromdate": convertDate(requisitionsFromDate),
                "todate": convertDate(requisitionsToDate)
            };

            // Set loading states and filters based on status
            if (requisitionStatus === "OPEN") {
                setTableDataLoadingOpen(true);
            } else if (requisitionStatus === "COVERED") {
                subStatusCode = openRequisitionsDataResponse?.subList.filter(item => item.subName === "COVERED")[0]?._id;
                selectedFilter.covered = subStatusCode;
                setTableDataLoadingCovered(true);
            } else if (requisitionStatus === "UNCOVERED") {
                subStatusCode = openRequisitionsDataResponse?.subList.filter(item => item.subName === "UNCOVERED")[0]?._id;
                selectedFilter.uncovered = subStatusCode;
                setTableDataLoadingUncovered(true);
            } else if (requisitionStatus === "UNASSIGNED") {
                subStatusCode = openRequisitionsDataResponse?.subList.filter(item => item.subName === "UNASSIGNED")[0]?._id;
                selectedFilter.unassigned = subStatusCode;
                setTableDataLoadingUnassigned(true);
            } else if (requisitionStatus === "FILLED") {
                setTableDataLoadingFilled(true);
            }

            let targetStatus = requisitionObj[requisitionStatus];
            let offset = isScroll ? targetStatus.offset : 0;
            let limit = isScroll ? targetStatus.limit : 5;
            let response;

            // API calls based on user type - Keep original logic
            if (userType === "Recruiter" || (userType === "Admin" && userRole === "Recruiter")) {
                response = await getAllRecruiterRequisitions(offset, limit, statusCode, selectedFilter, id, "", userRecruitmentpartnerId);
            } else if (userType === "Recruitment Leader" || (userType === "Admin" && userRole === "Recruitment Leader")) {
                response = await getRecruiterLeaderRequisitionsDetails(offset, limit, statusCode, selectedFilter, id, "", userRecruitmentpartnerId);
            } else if (userType === "Organization Admin" || (userType === "Admin" && userRole === "Organization Admin")) {
                let isOrgAdmin = true;
                response = await getRequisitionsDetails(offset, limit, statusCode, selectedFilter, userRecruitmentpartnerId, isOrgAdmin, "", userRecords.id);
            } else if (userType === "MIS Operator" || (userType === "Admin" && userRole === "MIS Operator")) {
                let isOrgAdmin = true;
                response = await getRequisitionsDetails(offset, limit, statusCode, selectedFilter, userRecruitmentpartnerId, isOrgAdmin, "", userRecords?.id);
            } else if (userType === "Team Leader" || (userType === "Admin" && userRole === "Team Leader")) {
                response = await getTeamLeaderRequisitionsDetails(offset, limit, statusCode, selectedFilter, id, "", userRecruitmentpartnerId);
            }

            // Process response data - Keep original logic
            let obj = { ...requisitionObj };
            let prevData = isScroll ? [...obj[requisitionStatus].data] : [];

            response?.data?.data?.requisitions?.forEach(item => {
                let resultObj = {};
                resultObj.requisitionId = item.requisitionId;
                resultObj.teamLeaderData = [];
                resultObj.teamDetails = [];
                resultObj.teamStatus = "";
                resultObj.reqreferenceId = item?.requisitionDetails?.reqreferenceId || item?.reqreferenceId;
                resultObj.primarySkill = item?.requisitionDetails?.primarySkill || item.primarySkill;

                // Process team leader data if exists
                if (item?.teamLeaderRequisitions?.length > 0) {
                    const data = item?.teamLeaderRequisitions;
                    for (let element of data) {
                        let statusObj = jsonConfig?.requisitionStatusCodes?.OPENSUBSTATUS;
                        let status = filterFunc(Object.keys(statusObj), (val) => statusObj[val] === element?.status?.subCode, element)?.at(0);
                        resultObj.teamDetails.push({ teamName: element?.teamdata?.teamName, status: status?.toLowerCase() });
                        resultObj.teamLeaderData.push({ teamLeaderName: element?.teamLeaderData?.displayName } || null);
                    }
                } else {
                    resultObj.isteamleader = false;
                }

                // Set client name based on user type
                if ((userType === "Organization Admin") ||
                    (userType === "Admin" && (userRole === "Organization Admin" || userRole === "MIS Operator")) ||
                    (userType === "MIS Operator")) {
                    resultObj.clientName = item.clientDetails?.clientName;
                } else {
                    resultObj.clientName = item.requisitionDetails?.clientDetails?.clientName;
                }

                resultObj.openings = (((userType !== "Recruiter" && userType !== "Admin") || (userType === "Admin" && userRole !== "Recruiter")) &&
                    ((userType !== "Team Leader") || (userType === "Admin" && userRole !== "Team Leader")))
                    ? item.openings : item.no_of_positions;

                resultObj.recruiterName = item?.recruiterData
                    ? item.recruiterData.map(item => item?.displayName).join(", ")
                    : "";

                resultObj.numberOfCandidateRequisitions = item.candidateRequisitions
                    ? item.candidateRequisitions.length
                    : "";

                // Add divisionDetails if userType is Organization Admin or MIS Operator
                if ((userType === "Organization Admin") ||
                    (userType === "MIS Operator") ||
                    (userType === "Admin" && (userRole === "Organization Admin" || userRole === "MIS Operator"))) {
                    resultObj.divisionDetails = item.divisionDetails;
                }

                prevData.push(resultObj);
            });

            obj[requisitionStatus] = { offset: offset + limit, limit: 5, data: prevData };
            setRequisitionObj({ ...obj });

            // Reset loading states
            if (requisitionStatus === "OPEN") {
                setTableDataLoadingOpen(false);
            } else if (requisitionStatus === "COVERED") {
                setTableDataLoadingCovered(false);
            } else if (requisitionStatus === "UNCOVERED") {
                setTableDataLoadingUncovered(false);
            } else if (requisitionStatus === "UNASSIGNED") {
                setTableDataLoadingUnassigned(false);
            } else if (requisitionStatus === "FILLED") {
                setTableDataLoadingFilled(false);
            }

        } catch (error) {
            console.log("Error", error);
        }
    };

    // Column definitions for TanStack Table
    const columnHelper = createColumnHelper();

    const getColumnsForStatus = (status) => {
        const baseColumns = [
            columnHelper.accessor('reqreferenceId', {
                header: 'Reference ID',
                cell: info => (
                    <div style={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {info.getValue()}
                    </div>
                )
            }),
            columnHelper.accessor('clientName', { header: 'Client Name' }),
            columnHelper.accessor('primarySkill', { header: 'Primary Skill' }),
            columnHelper.accessor('openings', { header: 'Openings' }),
        ];

        // Add conditional columns based on user type
        if (userType === "Organization Admin" || userType === "MIS Operator" ||
            (userType === "Admin" && (userRole === "Organization Admin" || userRole === "MIS Operator"))) {
            baseColumns.splice(2, 0, columnHelper.accessor('divisionDetails.divisionName', { header: 'Division Name' }));
        } else {
            baseColumns.splice(2, 0, columnHelper.accessor('recruiterName', {
                header: 'Recruiter Name',
                cell: info => {
                    const value = info.getValue();
                    if (value && value.length > 55) {
                        return (
                            <Tooltip title={value}>
                                <span>{value.slice(0, 55) + "..."}</span>
                            </Tooltip>
                        );
                    }
                    return value;
                }
            }));
        }

        if (status !== 'UNASSIGNED') {
            baseColumns.push(columnHelper.accessor('numberOfCandidateRequisitions', {
                header: 'Selected',
                cell: info => (
                    <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {info.getValue()}
                    </div>
                )
            }));
        }

        return baseColumns;
    };

    const tabTypes = ['filled', 'open', 'covered', 'uncovered', 'unassigned'];
    const statusMap = {
        'filled': 'FILLED',
        'open': 'OPEN',
        'covered': 'COVERED',
        'uncovered': 'UNCOVERED',
        'unassigned': 'UNASSIGNED'
    };

    const currentStatus = statusMap[tabTypes[activeTab]];
    const currentData = requisitionObj[currentStatus]?.data || [];
    const currentColumns = getColumnsForStatus(currentStatus);

    const table = useReactTable({
        data: currentData,
        columns: currentColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleCardClick = (cardType) => {
        const tabMap = { filled: 0, open: 1, covered: 2, uncovered: 3, unassigned: 4 };
        setActiveTab(tabMap[cardType] || 0);
        setDialogOpen(true);

        // Clear existing data and fetch new data
        const status = statusMap[cardType];
        setRequisitionObj(prev => ({
            ...prev,
            [status]: { offset: 0, limit: 5, data: [] }
        }));

        // Only fetch data if we have the required responses
        if ((status === "FILLED" && filledRequisitionDataResponse?.statusCode) ||
            (status !== "FILLED" && openRequisitionsDataResponse?.statusCode)) {
            getRequisitionData(status, false);
        }
    };

    const goToRequisition = (row) => {
        navigate(`/requisition-details/${row.requisitionId}`, {
            state: {
                id: id,
                userRecruitmentpartnerId: userRecruitmentpartnerId,
                userType: userType,
                userRecords: userRecords,
                userRole: userRole
            }
        });
    };

    const getHeaderRowStyle = (status) => {
        if (status === "OPEN") {
            return "open-header-row";
        }
        else if (status === "FILLED") {
            return "filled-header-row";
        }
        else if (status === "COVERED") {
            return "covered-header-row";
        }
        else if (status === "UNCOVERED") {
            return "uncovered-header-row";
        }
        else if (status === "UNASSIGNED") {
            return "unassigned-header-row";
        }
    }

    const getRowBackgroundStyle = (status) => {
        if (status === "FILLED") {
            return "filled-row-background";
        }
        else if (status === "OPEN") {
            return "open-row-background";
        }
        else if (status === "COVERED") {
            return "covered-row-background";
        }
        else if (status === "UNCOVERED") {
            return "uncovered-row-background";
        }
        else if (status === "UNASSIGNED") {
            return "unassigned-row-background";
        }
        return "";
    }
    const formDateChangeHandler = (date) => {
        setRequisitionsFromDate(date);
    };
    const toDateChangeHandler = (date) => {
        setRequisitionsToDate(date);
    };

    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        const status = statusMap[tabTypes[newValue]];

        // Reset data for this tab to start fresh
        setRequisitionObj(prev => ({
            ...prev,
            [status]: { offset: 0, limit: 5, data: [] }
        }));

        // Fetch fresh data for this tab
        if ((status === "FILLED" && filledRequisitionDataResponse?.statusCode) ||
            (status !== "FILLED" && openRequisitionsDataResponse?.statusCode)) {
            getRequisitionData(status, false);
        }
    };

    // Initialize requisition status data
    useEffect(() => {
        getAllrequistionStatusResponse();
    }, []);

    // Main data loading useEffect - CRITICAL FIX
    useEffect(() => {
        const loadUsers = async () => {
            if (!userRecruitmentpartnerId) return;

            try {
                setLoading(true);

                // Create payload like original
                let payload = {
                    "fromdate": convertDate(requisitionsFromDate),
                    "todate": convertDate(requisitionsToDate),
                    "isDashBoardCount": true,
                    "isOrgAdmin": false,
                    recruitmentpartnerId: userRecruitmentpartnerId
                };

                if (userType === "Organization Admin" || (userType === "Admin" && userRole === "Organization Admin")) {
                    payload.isOrgAdmin = true;
                    const requisitionResponse = await getAllRequisitionStatusAndCounts(userRecruitmentpartnerId, payload);
                    setRequisitionCountData(requisitionResponse.data.data);
                    setLoading(false);
                } else if (userType === "Recruiter" || (userType === "Admin" && userRole === "Recruiter")) {
                    const requisitionResponse = await getAllRecruiterRequisitionStatusAndCounts(id, payload);
                    setRequisitionCountData(requisitionResponse.data.data);
                    setLoading(false);
                } else if (userType === "Recruitment Leader" || (userType === "Admin" && userRole === "Recruitment Leader")) {
                    const requisitionResponse = await getAllRecruiterLeaderRequisitionStatusAndCounts(id, payload);
                    setRequisitionCountData(requisitionResponse.data.data);
                    setLoading(false);
                } else if (userType === "MIS Operator" || (userType === "Admin" && userRole === "MIS Operator")) {
                    payload.isMISOperator = true;
                    const requisitionResponse = await getAllRequisitionStatusAndCounts(userRecruitmentpartnerId, payload);
                    setRequisitionCountData(requisitionResponse.data.data);
                    setLoading(false);
                } else if (userType === "Team Leader" || (userType === "Admin" && userRole === "Team Leader")) {
                    const requisitionResponse = await getAllTeamLeaderRequisitionStatusAndCounts(id, payload);
                    setRequisitionCountData(requisitionResponse.data.data);
                    setLoading(false);
                }

            } catch (error) {
                console.log("Error", error);
                setLoading(false);
            }
        };

        // Only call if we have userRecruitmentpartnerId
        if (userRecruitmentpartnerId) {
            loadUsers();
        }
    }, [debouncedSearchStartDate, debouncedSearchEndDate, userType, userRecruitmentpartnerId, id, userRole, requisitionsFromDate, requisitionsToDate]);

    // Calculate progress bar widths
    const calculateProgress = (count) => {
        const total = Object.values(requisitionCount).reduce((sum, val) => sum + val, 0);
        return total > 0 ? Math.max((count / total) * 100, 5) : 0; // Minimum 5% for visibility
    };

    return (
        <>
            <div className="requisition-overview bento bento-wide bento-stats">
                <div className="bento-header">
                    <div className="bento-header-content">
                        <div className="bento-header-text">
                            <span>Requisitions Overview</span>
                        </div>
                        <div className="bento-header-date">
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div className='date-pill-reqview'>
                                    <DatePicker
                                        selected={requisitionsFromDate}
                                        className="date-pill-input-reqview"
                                        value={requisitionsFromDate || ''}
                                        onChange={formDateChangeHandler}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="DD-MM-YYYY"
                                        showIcon
                                    />
                                </div>

                                <div className='date-pill-reqview'>
                                    <DatePicker
                                        selected={requisitionsToDate}
                                        className="date-pill-input-reqview"
                                        value={requisitionsToDate || ''}
                                        onChange={toDateChangeHandler}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="DD-MM-YYYY"
                                        showIcon
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dashboard_bento_grid">
                    <div
                        className="bento_card status_filled"
                        onClick={() => loading ? null : handleCardClick('filled')}
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                    >
                        <div className="bento_card_header">
                            <span className="bento_dot dot_green" />
                            <span className="bento_count">
                                {loading ? <CircularProgress size={16} /> : requisitionCount.FILLED}
                            </span>
                        </div>
                        <div className="bento_label">Filled</div>
                        <div className="bento_progress_bar">
                            <div className="bento_progress progress_green" style={{ width: `${calculateProgress(requisitionCount.FILLED)}%` }} />
                        </div>
                    </div>

                    <div
                        className="bento_card status_open"
                        onClick={() => loading ? null : handleCardClick('open')}
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                    >
                        <div className="bento_card_header">
                            <span className="bento_dot dot_blue" />
                            <span className="bento_count">
                                {loading ? <CircularProgress size={16} /> : requisitionCount.OPEN}
                            </span>
                        </div>
                        <div className="bento_label">Open</div>
                        <div className="bento_progress_bar">
                            <div className="bento_progress progress_blue" style={{ width: `${calculateProgress(requisitionCount.OPEN)}%` }} />
                        </div>
                    </div>

                    <div
                        className="bento_card status_covered"
                        onClick={() => loading ? null : handleCardClick('covered')}
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                    >
                        <div className="bento_card_header">
                            <span className="bento_dot dot_cyan" />
                            <span className="bento_count">
                                {loading ? <CircularProgress size={16} /> : requisitionCount.COVERED}
                            </span>
                        </div>
                        <div className="bento_label">Covered</div>
                        <div className="bento_progress_bar">
                            <div className="bento_progress progress_cyan" style={{ width: `${calculateProgress(requisitionCount.COVERED)}%` }} />
                        </div>
                    </div>

                    <div
                        className="bento_card status_uncovered"
                        onClick={() => loading ? null : handleCardClick('uncovered')}
                        style={{ cursor: loading ? 'default' : 'pointer' }}
                    >
                        <div className="bento_card_header">
                            <span className="bento_dot dot_orange" />
                            <span className="bento_count">
                                {loading ? <CircularProgress size={16} /> : requisitionCount.UNCOVERED}
                            </span>
                        </div>
                        <div className="bento_label">Uncovered</div>
                        <div className="bento_progress_bar">
                            <div className="bento_progress progress_orange" style={{ width: `${calculateProgress(requisitionCount.UNCOVERED)}%` }} />
                        </div>
                    </div>

                    {(userType !== "Recruiter" && !(userType === "Admin" && userRole === "Recruiter")) && (
                        <div
                            className="bento_card status_unassigned"
                            onClick={() => loading ? null : handleCardClick('unassigned')}
                            style={{ cursor: loading ? 'default' : 'pointer' }}
                        >
                            <div className="bento_card_header">
                                <span className="bento_dot dot_red" />
                                <span className="bento_count">
                                    {loading ? <CircularProgress size={16} /> : requisitionCount.UNASSIGNED}
                                </span>
                            </div>
                            <div className="bento_label">Unassigned</div>
                            <div className="bento_progress_bar">
                                <div className="bento_progress progress_red" style={{ width: `${calculateProgress(requisitionCount.UNASSIGNED)}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                className='dialog_requisitionOverview'
            >
                <DialogTitle className='dialogtitle-requisitionOverview'>
                    <div className='titleContainer_requisitionOverview'>
                        <span>Requisitions Details</span>
                        <div className="bento-header-date">
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <TextField
                                    type="date"
                                    // label="From"
                                    value={requisitionsFromDate || ''}
                                    onChange={(e) => setRequisitionsFromDate(e.target.value)}
                                    size="small"
                                    className="date-picker-textfield"
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        style: { cursor: 'pointer' }
                                    }}
                                />
                                <TextField
                                    type="date"
                                    // label="To"
                                    value={requisitionsToDate || ''}
                                    onChange={(e) => setRequisitionsToDate(e.target.value)}
                                    size="small"
                                    className="date-picker-textfield"
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        style: { cursor: 'pointer' }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </DialogTitle>

                <DialogContent className='dialogcontent-requisitionOverview'>

                    <div className="requisition-tabs-container">
                        <div className="requisition-tabs">
                            <div
                                className={`requisition-tab ${activeTab === 0 ? 'Mui-selected' : ''}`}
                                onClick={() => handleTabChange(0)}
                            >
                                Filled
                            </div>
                            <div
                                className={`requisition-tab ${activeTab === 1 ? 'Mui-selected' : ''}`}
                                onClick={() => handleTabChange(1)}
                            >
                                Open
                            </div>
                            <div
                                className={`requisition-tab ${activeTab === 2 ? 'Mui-selected' : ''}`}
                                onClick={() => handleTabChange(2)}
                            >
                                Covered
                            </div>
                            <div
                                className={`requisition-tab ${activeTab === 3 ? 'Mui-selected' : ''}`}
                                onClick={() => handleTabChange(3)}
                            >
                                Uncovered
                            </div>
                            {(userType !== "Recruiter" && !(userType === "Admin" && userRole === "Recruiter")) && (
                                <div
                                    className={`requisition-tab ${activeTab === 4 ? 'Mui-selected' : ''}`}
                                    onClick={() => handleTabChange(4)}
                                >
                                    Unassigned
                                </div>
                            )}
                        </div>
                    </div>

                    <Box className="requisition-table-container">
                        <div className="requisition-table-header">
                            <table className='header-table-requisitionOverview'>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}
                                            className={`header-row-requisition-overview ${getHeaderRowStyle(currentStatus)}`}
                                        >
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id} style={{ textAlign: 'left' }}>
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                            </table>
                        </div>

                        <div className="requisition-table-body">
                            <table className='body-table-requisitionOverview'>
                                <tbody>
                                    {table.getRowModel().rows.map(row => (
                                        <tr
                                            key={row.id}
                                            className={`requisition-table-row ${getRowBackgroundStyle(currentStatus)}`}
                                            onClick={() => goToRequisition(row.original)}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id}>
                                                    {currentStatus === 'UNASSIGNED' ? (
                                                        <Tooltip
                                                            title={
                                                                userType !== "Team Leader" ?
                                                                    row.original?.isteamleader === false ?
                                                                        "Team not Assigned" :
                                                                        row.original?.teamDetails?.map(name => `Assigned to ${name.teamName}, Status: ${name.status}`).join(", ") :
                                                                    ""
                                                            }
                                                        >
                                                            <div>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {/* Loading skeleton rows */}
                                    {(tableDataLoadingFilled || tableDataLoadingOpen || tableDataLoadingCovered ||
                                        tableDataLoadingUncovered || tableDataLoadingUnassigned) &&
                                        [...Array(5)].map((_, index) => (
                                            <tr key={`skeleton-${index}`}>
                                                {table.getHeaderGroups()[0].headers.map((header, colIndex) => (
                                                    <td key={`skeleton-${index}-${colIndex}`}>
                                                        <Skeleton variant="text" height={20} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    }

                                    {/* No data row */}
                                    {currentData.length === 0 && !loading &&
                                        !(tableDataLoadingFilled || tableDataLoadingOpen || tableDataLoadingCovered ||
                                            tableDataLoadingUncovered || tableDataLoadingUnassigned) && (
                                            <tr>
                                                <td
                                                    colSpan={table.getHeaderGroups()[0].headers.length}
                                                    className='no-data-td-requisition-overview'
                                                >
                                                    No data found for this category
                                                </td>
                                            </tr>
                                        )}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More Button Section */}
                        {currentData.length > 0 &&
                            requisitionCount[currentStatus] > currentData.length && (
                                <div className="load-more-container">
                                    {currentData.length > 0 &&
                                        requisitionCount[currentStatus] > currentData.length &&
                                        !(tableDataLoadingFilled || tableDataLoadingOpen || tableDataLoadingCovered ||
                                            tableDataLoadingUncovered || tableDataLoadingUnassigned) && (
                                            <Button
                                                variant="contained"
                                                onClick={() => getRequisitionData(currentStatus, true)}
                                                className="load-more-button"
                                            >
                                                Load more ({requisitionCount[currentStatus] - currentData.length} remaining)
                                            </Button>
                                        )}

                                    {/* Loading indicator when fetching more data */}
                                    {(tableDataLoadingFilled || tableDataLoadingOpen || tableDataLoadingCovered ||
                                        tableDataLoadingUncovered || tableDataLoadingUnassigned) && currentData.length > 0 && (
                                            <div className="requisition-loading-container">
                                                <CircularProgress size={24} />
                                                <span style={{ marginLeft: '8px' }}>Loading more...</span>
                                            </div>
                                        )}
                                </div>
                            )}
                    </Box>
                </DialogContent>
            </Dialog>

        </>
    );
};

export default RequisitionsOverview;
